import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";

import { SpinnerLoader } from "@ucchub/ui";

import AuthBox from "@components/auth/AuthBox";
import Logout from "@components/auth/Logout";
import CustomerView from "@pages/CustomerManagement/CustomerView/CustomerView";
import Customers from "@pages/CustomerManagement/Customers";
import ProductConfiguration from "@pages/CustomerManagement/ProductConfiguration/ProductConfiguration";
import IsdraftCustomer from "@pages/NewCustomerManagement/IsdraftCustomer/IsdraftCustomer";
import NewAddCustomer from "@pages/NewCustomerManagement/NewCustomerAdd/NewAddCustomer";
import NewEditCustomer from "@pages/NewCustomerManagement/NewEditCustomer/NewEditCustomer";
import AddRole from "@pages/RoleManagement/AddRole/AddRole";
import ViewRole from "@pages/RoleManagement/ViewRole";
import Roles from "@pages/RoleManagement/roles";
import UsersAdd from "@pages/UserManagement/AddUser/AddUser";
import EditUser from "@pages/UserManagement/EditUser/EditUser";
import UserManagement from "@pages/UserManagement/UserManagement";
import ViewUser from "@pages/UserManagement/ViewUser/ViewUser";
import ForgotPassword from "@pages/forgotpassword";
import Home from "@pages/home";
import NotFound from "@pages/notfound";
import Support from "@pages/support";

import ProtectedRoute from "./auth/ProtectedRoute";
import DashShell from "./common/DashShell";

// Remote Container Imports via Module Federation
// Cloud Numbers
const CNProvider = lazy(() => import("cloud_numbers/Provider"));
const CNLanding = lazy(() => import("cloud_numbers/LandingPage"));
const CNCountryOverview = lazy(() => import("cloud_numbers/CountryOverview"));
const CNCountryManage = lazy(() => import("cloud_numbers/CountryManage"));
const CNNumberRequest = lazy(() => import("cloud_numbers/NumberRequest"));
const CNOrderComments = lazy(() => import("cloud_numbers/OrderComments"));
const CNOrdersList = lazy(() => import("cloud_numbers/OrdersList"));
const CNOrderDetails = lazy(() => import("cloud_numbers/OrderDetails"));
const CNRegulationsView = lazy(() => import("cloud_numbers/RegulationsView"));
const CNMyNumbers = lazy(() => import("cloud_numbers/MyNumbers"));
const CNEndCustomerView = lazy(
  () => import("cloud_numbers/EndCustomerDetails")
);
const CNEndCustomerEdit = lazy(
  () => import("cloud_numbers/EditEndCustomerDetails")
);
const CNCeaseNumbers = lazy(() => import("cloud_numbers/CeaseNumbers"));
const CNCeaseSuccess = lazy(() => import("cloud_numbers/CeaseSuccess"));
const CNAllocateNumbers = lazy(() => import("cloud_numbers/AllocateNumbers"));
const CNEndCustomersList = lazy(() => import("cloud_numbers/EndCustomersList"));
const CNEndCustomersAdd = lazy(() => import("cloud_numbers/EndCustomersAdd"));
const CNEndCustomersAddServices = lazy(
  () => import("cloud_numbers/EndCustomersAddServices")
);
const CNRegulatoryBundleList = lazy(
  () => import("cloud_numbers/RegulatoryBundleList")
);

const CNBundlesManagementList = lazy(
  () => import("cloud_numbers/BundlesManagementList")
);

const CNBundleManagementDetails = lazy(
  () => import("cloud_numbers/BundleManagementDetails")
);
//BundleRouting
const CNAddBundle = lazy(() => import("cloud_numbers/AddBundle"));
const CNEditBundle = lazy(() => import("cloud_numbers/EditBundle"));
const CNEditBundleSuccess = lazy(
  () => import("cloud_numbers/EditBundleSuccess")
);

const CNRegulatoryBundleView = lazy(
  () => import("cloud_numbers/RegulatoryBundleView")
);

const CNBundleManagementSuccess = lazy(
  () => import("cloud_numbers/BundleManagementSuccess")
);

const InnerRoutes = () => {
  const { t } = useTranslation();

  return (
    <Routes>
      <Route path="*" element={<NotFound />} />

      <Route path="/">
        <Route index element={<Home />} />

        <Route
          path="/login"
          element={
            <>
              <Home />
              <AuthBox isOpen={true} />
            </>
          }
        />

        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route element={<ProtectedRoute />}>
          <Route path="/support" element={<Support />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/users" element={<UserManagement />} />
          <Route path="/users/add" element={<UsersAdd />} />
          <Route path="/roles" element={<Roles />} />
          <Route path="/roles/add" element={<AddRole />} />
          <Route path="/roles/:roleId" element={<ViewRole />} />
          <Route path="/users/:userId" element={<ViewUser />} />
          <Route path="/users/:email/edit" element={<EditUser />} />
          <Route path="/customers" element={<Customers />} />
          <Route
            path="/customers/:customerId/view"
            element={<CustomerView />}
          />
          <Route
            path="/customers/:customerId/product-config/:productId"
            element={<ProductConfiguration mode="view" />}
          />
          <Route
            path="/customers/:customerId/product-config/:productId/add"
            element={<ProductConfiguration mode="add" />}
          />
          <Route path="/customers/add-new" element={<NewAddCustomer />} />
          <Route
            path="/customers/:customerId/edit-new"
            element={<NewEditCustomer />}
          />
          <Route
            path="/customers/:customerId/draft"
            element={<IsdraftCustomer />}
          />
        </Route>
      </Route>

      {/* Cloud Numbers Routes */}
      <Route element={<ProtectedRoute />}>
        <Route
          path="/cloud-numbers"
          element={
            <Suspense fallback={null}>
              <CNProvider>
                <DashShell />
              </CNProvider>
            </Suspense>
          }
        >
          <Route path="/cloud-numbers/add-bundle" element={<CNAddBundle />} />
          <Route
            path="/cloud-numbers"
            element={
              <Suspense
                fallback={
                  <SpinnerLoader
                    srText={
                      t("core:common:loading_module:cloud_numbers") as string
                    }
                    text={
                      t("core:common:loading_module:cloud_numbers") as string
                    }
                  />
                }
              >
                <Outlet />
              </Suspense>
            }
          >
            <Route index element={<CNLanding />} />
            <Route
              path="/cloud-numbers/countries"
              element={<CNCountryOverview />}
            />
            <Route path="/cloud-numbers/add-bundle" element={<CNAddBundle />} />
            <Route
              path="/cloud-numbers/add-bundle/:id"
              element={<CNEditBundle />}
            />
            <Route
              path="/cloud-numbers/regulatory-bundles/success"
              element={<CNEditBundleSuccess />}
            />
            <Route
              path="/cloud-numbers/country/:id"
              element={<CNCountryManage />}
            />
            <Route
              path="/cloud-numbers/numbers/request"
              element={<CNNumberRequest />}
            />
            <Route path="/cloud-numbers/orders" element={<CNOrdersList />} />
            <Route
              path="/cloud-numbers/orders/:id"
              element={<CNOrderDetails />}
            />
            <Route
              path="/cloud-numbers/regulations/"
              element={<CNRegulationsView />}
            />
            <Route
              path="/cloud-numbers/regulations/:countryCode/:numberType"
              element={<CNRegulationsView />}
            />
            <Route
              path="/cloud-numbers/orders/:id/comments/:conversationId"
              element={<CNOrderComments />}
            />
            <Route path="/cloud-numbers/numbers" element={<CNMyNumbers />} />
            <Route
              path="/cloud-numbers/end-customer/:endCustomerId"
              element={<CNEndCustomerView />}
            />
            <Route
              path="/cloud-numbers/end-customer/:endCustomerId/comments/:conversationId"
              element={<CNOrderComments />}
            />
            <Route
              path="/cloud-numbers/end-customer/:endCustomerId/edit"
              element={<CNEndCustomerEdit />}
            />
            <Route
              path="/cloud-numbers/numbers/cease"
              element={<CNCeaseNumbers />}
            />
            <Route
              path="/cloud-numbers/numbers/cease/success/:requestId"
              element={<CNCeaseSuccess />}
            />
            <Route
              path="/cloud-numbers/numbers/allocate"
              element={<CNAllocateNumbers />}
            />
            <Route
              path="/cloud-numbers/endcustomer/"
              element={<CNEndCustomersList />}
            />
            <Route
              path="/cloud-numbers/endcustomer/add"
              element={<CNEndCustomersAdd />}
            />
            <Route
              path="/cloud-numbers/endcustomer/services/:id"
              element={<CNEndCustomersAddServices />}
            />
            <Route
              path="/cloud-numbers/regulatory-bundles/:id"
              element={<CNRegulatoryBundleView />}
            />
            <Route
              path="/cloud-numbers/bundles-management"
              element={<CNBundlesManagementList />}
            />
            <Route
              path="/cloud-numbers/bundles-management/:id"
              element={<CNBundleManagementDetails />}
            />
            <Route
              path="/cloud-numbers/regulatory-bundles"
              element={<CNRegulatoryBundleList />}
            />
            <Route
              path="/cloud-numbers/bundles-management/success/:bundleName/:status"
              element={<CNBundleManagementSuccess />}
            />
          </Route>
        </Route>
      </Route>
      {/* End of Cloud Numbers Routes */}
    </Routes>
  );
};

const Router = () => {
  return (
    <BrowserRouter>
      <InnerRoutes />
    </BrowserRouter>
  );
};

export default Router;
